import { z } from "zod";
// Value import (not type-only): the Zod enum is built from the same tuple the
// PromotionDto is typed by, so the two can never drift — the `STAGE_ROLES`
// precedent in this folder's index.
import { PROMOTION_KINDS } from "../types/index.js";
/**
 * Promotions — the one definition of a valid pricing window, shared by the CRM
 * editor and the route body so the two cannot drift.
 *
 * A Pattern-B schema (like `catalogPayloadSchema` and `waba-templates.ts`): it is
 * `safeParse`d in the browser to render inline errors AND attached as the Fastify
 * body schema. Messages are Spanish because they surface verbatim in the editor
 * and in CRM toasts.
 *
 * Every rule here exists because the value on the other side of it is a price a
 * real person is quoted and then charged.
 */
export const promotionKindSchema = z.enum(PROMOTION_KINDS);
/**
 * Minutes since local midnight.
 *
 * Deliberately NOT `multipleOf(30)` like `visits.ts`: that granularity is a slot
 * engine simplification, and a shop's price change at 17:45 is an ordinary thing
 * to want. 1440 is accepted as an exclusive end-of-day ("hasta medianoche").
 */
const minuteOfDaySchema = z.number().int().min(0).max(1440);
const dateStrSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha inválido (AAAA-MM-DD)");
/** 0 = Sunday, matching JS `Date#getDay` and the slot engine. */
const weekdaySchema = z.number().int().min(0).max(6);
/**
 * A price ceiling on the override.
 *
 * Not a claim about what anything costs — it is the blast radius of a typo in a
 * field that sets what a customer pays. $1,000,000.00 in minor units.
 */
export const MAX_PROMOTION_PRICE_CENTS = 100_000_000;
/** 10000 basis points = 100 %. Two decimals of a percent, in integers. */
export const MAX_PERCENT_OFF_BP = 10_000;
/** One promotion cannot name more products than a catalog plausibly holds. */
export const MAX_PROMOTION_PRODUCTS = 200;
export const promotionInputSchema = z
    .object({
    name: z
        .string()
        .trim()
        .min(1, "El nombre es obligatorio")
        .max(120, "Máximo 120 caracteres"),
    kind: promotionKindSchema,
    overridePriceCents: z
        .number()
        .int("El precio debe ser un número entero de centavos")
        .min(0)
        .max(MAX_PROMOTION_PRICE_CENTS, "El precio es demasiado alto")
        .nullable()
        .default(null),
    percentOffBp: z
        .number()
        .int()
        .min(1, "El descuento debe ser mayor que cero")
        .max(MAX_PERCENT_OFF_BP, "El descuento no puede pasar de 100%")
        .nullable()
        .default(null),
    dateFrom: dateStrSchema.nullable().default(null),
    dateTo: dateStrSchema.nullable().default(null),
    /** EMPTY = every day. There is no null here, so there is no null-vs-empty bug. */
    weekdays: z.array(weekdaySchema).max(7).default([]),
    startMinute: minuteOfDaySchema.nullable().default(null),
    endMinute: minuteOfDaySchema.nullable().default(null),
    /** Whether the agent may tell the customer a promotion is why the price moved. */
    announce: z.boolean().default(true),
    productIds: z
        .array(z.string().uuid())
        .min(1, "Elige al menos un producto")
        .max(MAX_PROMOTION_PRODUCTS),
})
    .superRefine((p, ctx) => {
    // The amount field must match the kind, in BOTH directions. Accepting a
    // stray value on the unused field would let an edit that switches kind carry
    // the old amount forward invisibly.
    if (p.kind === "price_override") {
        if (p.overridePriceCents === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Escribe el precio de la promoción",
                path: ["overridePriceCents"],
            });
        }
        if (p.percentOffBp !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Un precio fijo no lleva porcentaje",
                path: ["percentOffBp"],
            });
        }
    }
    else {
        if (p.percentOffBp === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Escribe el porcentaje de descuento",
                path: ["percentOffBp"],
            });
        }
        if (p.overridePriceCents !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Un descuento por porcentaje no lleva precio fijo",
                path: ["overridePriceCents"],
            });
        }
    }
    // ISO date strings compare correctly as strings — the `visits.ts` precedent.
    if (p.dateFrom !== null && p.dateTo !== null && p.dateFrom > p.dateTo) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "La fecha de fin no puede ser anterior a la de inicio",
            path: ["dateTo"],
        });
    }
    const hasStart = p.startMinute !== null;
    const hasEnd = p.endMinute !== null;
    if (hasStart !== hasEnd) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Escribe la hora de inicio y la de fin, o deja ambas vacías",
            path: ["endMinute"],
        });
    }
    if (hasStart && hasEnd && p.startMinute >= p.endMinute) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "La hora de fin debe ser posterior a la de inicio",
            path: ["endMinute"],
        });
    }
    if (new Set(p.weekdays).size !== p.weekdays.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Hay días repetidos",
            path: ["weekdays"],
        });
    }
    if (new Set(p.productIds).size !== p.productIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Hay productos repetidos",
            path: ["productIds"],
        });
    }
});
/**
 * An update carries the whole promotion, not a patch.
 *
 * The same "the payload is the desired end state" rule `planRepository.update`
 * follows for feature links: the product set is replaced wholesale, so a product
 * dropped from the list is a product dropped from the promotion.
 */
export const promotionUpdateSchema = promotionInputSchema;
export const promotionActiveSchema = z.object({
    isActive: z.boolean(),
});
//# sourceMappingURL=promotions.js.map